'use client';

import { useId, useState } from 'react';

import { PASSWORD_COPY } from '@eliadmin/core/src/components/dlz/account/password/password-copy';
import { RESET_COPY } from '@eliadmin/core/src/components/dlz/auth/password-reset/password-reset-copy';
import {
  formatCountdown,
  useResendCooldown,
} from '@eliadmin/core/src/components/dlz/auth/password-reset/use-resend-cooldown';
import { useSendResetLink } from '@eliadmin/core/src/components/dlz/auth/password-reset/use-send-reset-link';
import { cn } from '@eliadmin/core/src/utils/style-utils';

type SendStatus = 'idle' | 'sending' | 'failed';

const ACTION =
  'dlz-body-s-medium self-start rounded-sm text-[#D7191F] transition-colors hover:text-[#B5141A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012] disabled:cursor-not-allowed disabled:text-[#9096A0]';

/**
 * Figma „Forgot“ (60322:20025; mobile 60459:90362): the reset link of the forgotten-password flow
 * sent to the account e-mail. Once sent, the text confirms it and the action resends after the same
 * pause as the dialog („Znova môžete poslať o 0:45“).
 */
export function ForgotPasswordCard({ email }: { email: string }) {
  const titleId = useId();
  const sendLink = useSendResetLink();
  const [status, setStatus] = useState<SendStatus>('idle');
  const [sent, setSent] = useState(false);
  const { remaining, restart } = useResendCooldown(false);
  const copy = PASSWORD_COPY.forgot;

  const send = async () => {
    setStatus('sending');
    try {
      await sendLink(email);
      restart();
      setSent(true);
      setStatus('idle');
    } catch {
      setStatus('failed');
    }
  };

  return (
    <section
      aria-labelledby={titleId}
      className="flex flex-col gap-6 rounded-[18px] border border-[#E2E4E7] bg-white p-4 md:p-6"
    >
      <div className="flex flex-col gap-2">
        <h2 id={titleId} className="dlz-body-s-medium text-[#0F1012]">
          {copy.title}
        </h2>
        <p aria-live="polite" className="dlz-body-s text-[#4F545C]">
          {sent ? copy.sent(email) : copy.text(email)}
        </p>
        {status === 'failed' ? (
          <p role="alert" className="dlz-body-s text-[#D7191F]">
            {copy.failed}
          </p>
        ) : null}
      </div>
      <div className="flex flex-col gap-1">
        <button
          type="button"
          onClick={send}
          disabled={status === 'sending' || remaining > 0}
          aria-busy={status === 'sending' || undefined}
          className={cn(ACTION, status === 'sending' && 'cursor-wait')}
        >
          {sent ? RESET_COPY.sent.resend : RESET_COPY.email.submit}
        </button>
        {remaining > 0 ? (
          <p className="dlz-caption text-[#6B7079]">
            {RESET_COPY.sent.countdown(formatCountdown(remaining))}
          </p>
        ) : null}
      </div>
    </section>
  );
}
